/**
 * CR316: Recent Searches Hook
 * 
 * Client-side storage for recent search terms, scoped by preset ID.
 * Uses localStorage for persistence.
 */

import { useState, useCallback, useEffect } from "react";
import type { RecentSearchesConfig } from "@/lib/layout-studio/types";
import { DEFAULT_RECENT_SEARCHES_CONFIG } from "@/lib/layout-studio/types";

const STORAGE_KEY_PREFIX = "dealFinder:recentSearches:";

interface UseRecentSearchesOptions {
  presetId: string | null;
  config?: RecentSearchesConfig;
  minChars?: number; // Minimum characters to save a search
}

export function useRecentSearches({ 
  presetId, 
  config,
  minChars = 1 
}: UseRecentSearchesOptions) {
  const mergedConfig = { ...DEFAULT_RECENT_SEARCHES_CONFIG, ...config };
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  // Build storage key
  const storageKey = presetId ? `${STORAGE_KEY_PREFIX}${presetId}` : null;

  // Load from localStorage on mount
  useEffect(() => {
    if (!storageKey || !mergedConfig.enabled) {
      setRecentSearches([]);
      return;
    }

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setRecentSearches(parsed.slice(0, mergedConfig.maxItems));
        }
      }
    } catch (e) {
      console.warn("Failed to load recent searches:", e);
      setRecentSearches([]);
    }
  }, [storageKey, mergedConfig.enabled, mergedConfig.maxItems]);

  // Add a search term
  const addSearch = useCallback((term: string) => {
    if (!storageKey || !mergedConfig.enabled) return;

    // Normalize: trim and collapse spaces
    const normalized = term.trim().replace(/\s+/g, " ");
    
    // Skip if too short
    if (normalized.length < minChars) return;

    setRecentSearches((prev) => {
      // Remove existing instance (for deduplication and move-to-top)
      const filtered = prev.filter(
        (s) => s.toLowerCase() !== normalized.toLowerCase()
      );
      
      // Add to front, limit to maxItems
      const updated = [normalized, ...filtered].slice(0, mergedConfig.maxItems);
      
      // Persist
      try {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      } catch (e) {
        console.warn("Failed to save recent searches:", e);
      }
      
      return updated;
    });
  }, [storageKey, mergedConfig.enabled, mergedConfig.maxItems, minChars]);

  // Clear all recent searches
  const clearAll = useCallback(() => {
    if (!storageKey) return;
    
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.warn("Failed to clear recent searches:", e);
    }
    
    setRecentSearches([]);
  }, [storageKey]);

  // Remove a single search term
  const removeSearch = useCallback((term: string) => {
    if (!storageKey) return;
    
    setRecentSearches((prev) => {
      const updated = prev.filter(
        (s) => s.toLowerCase() !== term.toLowerCase()
      );
      
      try {
        if (updated.length > 0) {
          localStorage.setItem(storageKey, JSON.stringify(updated));
        } else {
          localStorage.removeItem(storageKey);
        }
      } catch (e) {
        console.warn("Failed to update recent searches:", e);
      }
      
      return updated;
    });
  }, [storageKey]);

  return {
    recentSearches,
    addSearch,
    removeSearch,
    clearAll,
    enabled: mergedConfig.enabled,
    config: mergedConfig,
  };
}
